import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DirectoryFilters from "@/components/directory/DirectoryFilters";
import SubtypeChips, {
  filterBySubtype,
} from "@/components/directory/SubtypeChips";
import ListingGrid from "@/components/directory/ListingGrid";
import { useListings, useFacets } from "@/api/listings";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n/LangContext";

const EYEBROW_CAT = { nl: "Categorie", de: "Kategorie", en: "Category" };
const EYEBROW_OVERVIEW = { nl: "Overzicht", de: "Übersicht", en: "Overview" };

/**
 * Bedrijvengids pagina.
 * Ondersteunt optionele lockedCategory via prop of ?category= param voor hergebruik door categorie-pagina's.
 * Fijnere subtype-chips (bv. Restaurant/Café/IJs) filteren client-side op naam-keywords.
 */
export const BedrijvengidsPage = ({
  lockedCategory = null,
  heading,
  intro,
  eyebrow,
}) => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const [searchParams] = useSearchParams();
  const initialCategory = lockedCategory || searchParams.get("category") || "";

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState(initialCategory);
  const [subtype, setSubtype] = useState("");

  // Eén willekeurige seed per volledige paginalading van de gids: de volgorde
  // binnen elke laag rouleert bij elk bezoek/herlaad, maar blijft binnen deze
  // laden stabiel (ook bij filteren/meerdere calls — paginering-veilig).
  const [seed] = useState(
    () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
  );

  // Reset subtype als je van categorie wisselt.
  const effectiveCategory = lockedCategory || category;
  React.useEffect(() => {
    setSubtype("");
  }, [effectiveCategory]);

  const facets = useFacets();
  const { items, loading } = useListings({
    q,
    category: effectiveCategory,
    seed,
  });

  const filteredItems = useMemo(
    () => filterBySubtype(items, effectiveCategory, subtype),
    [items, effectiveCategory, subtype],
  );

  const eyebrowLabel =
    eyebrow ||
    (lockedCategory
      ? EYEBROW_CAT[lang] || EYEBROW_CAT.nl
      : EYEBROW_OVERVIEW[lang] || EYEBROW_OVERVIEW.nl);

  const pageTitle = heading ? `${heading} — Lemmer Leeft` : t("nav.bedrijvengids");

  return (
    <div data-testid="bedrijvengids-page" className="bg-white">
      <Seo title={pageTitle} description={intro} lang={lang} />
      {/* Header sectie */}
      <section className="relative ll-section-soft py-12 sm:py-16 overflow-hidden">
        <div className="ll-container max-w-4xl relative z-10">
          <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
            {eyebrowLabel}
          </span>
          <h1
            data-testid="category-heading"
            className="text-4xl sm:text-5xl font-bold mb-4"
          >
            {heading}
          </h1>
          {/* accentlijn + golf-decoratie */}
          <div className="flex items-center gap-3 mb-5" aria-hidden="true">
            <span className="block w-10 h-[3px] rounded-full bg-[#2E9CD6]" />
            <svg
              width="60"
              height="10"
              viewBox="0 0 60 10"
              className="text-[#6BC0E8]"
            >
              <path
                d="M0,5 C10,0 20,10 30,5 C40,0 50,10 60,5"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p
            data-testid="category-intro"
            className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed max-w-2xl"
          >
            {intro}
          </p>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="py-10 sm:py-12">
        <div className="ll-container">
          <DirectoryFilters
            q={q}
            setQ={setQ}
            category={category}
            setCategory={setCategory}
            facets={facets}
            lockedCategory={lockedCategory}
          />
          <SubtypeChips
            category={effectiveCategory}
            items={items}
            activeKey={subtype}
            onChange={setSubtype}
            lang={lang}
          />
          <ListingGrid
            items={filteredItems}
            total={filteredItems.length}
            loading={loading}
          />
        </div>
      </section>
    </div>
  );
};

export default BedrijvengidsPage;
